import type { FlowState } from '@de/providers/dynamicExperienceReducer';
import type { Option, Step } from '@de/types/types';
import { buildBoundComponentIndex } from './findBoundComponent';
import type { BoundComponentInfo } from './findBoundComponent';
import { buildOptionFromFormState } from './buildOptionFromFormState';

/**
 * Host `initialFormState` is a mount seed, not a controlled value.
 * Blank keys, empty strings, and non-string values are dropped so they
 * never mark a key as already set.
 */
export function sanitizeInitialFormState(
  input: Readonly<Record<string, unknown>> | undefined,
): Readonly<Record<string, string>> {
  if (!input) return {};
  const seed: Record<string, string> = {};
  for (const [key, value] of Object.entries(input)) {
    if (key.trim().length === 0) continue;
    if (typeof value !== 'string' || value.length === 0) continue;
    seed[key] = value;
  }
  return seed;
}

/**
 * Entry step, plus the jump-to landing step when it is a different id.
 * Same resolution `initializeAutoPopulation` uses for the DCS triad, so
 * a deep link and a normal entry agree on where a seed is allowed to land.
 */
export function resolveLandingStepIds(
  steps: readonly Step[],
  entryStepId: string | undefined,
  jumpToStepId: string | undefined,
): readonly string[] {
  const entry = steps.find((step) => step.isEntry === true) ?? steps[0];
  const entryId = entryStepId || entry?.id;
  const ids: string[] = [];
  if (entryId) ids.push(entryId);
  if (jumpToStepId && jumpToStepId !== entryId) ids.push(jumpToStepId);
  return ids;
}

export interface HostOwnedFormSeed {
  readonly flowState: FlowState;
  /**
   * Form keys this call wrote from the host seed, keyed by landing step id.
   * A key is absent when a recorded, DCS, or preselected value already owned
   * it, and unbound host keys are never listed.
   */
  readonly appliedKeysByStep: Readonly<Record<string, readonly string[]>>;
}

/**
 * Write host-owned keys onto the entry step and the jump-to landing step.
 *
 * A key is owned when a control on that step declares the same `formKey`.
 * Owned keys become flowState answers so branching, lead inputs, and
 * `{{context.*}}` see them. A key with no control on that step is left
 * out — the caller keeps it for the Data Source Manager only.
 *
 * A selection already on the step wins for the same key. Call this after
 * the DCS / preselected seed so those values outrank the host.
 */
export function seedInitialFormState(
  flowState: FlowState,
  steps: readonly Step[],
  hostInitialFormState: Readonly<Record<string, string>>,
  entryStepId?: string,
  jumpToStepId?: string,
): FlowState {
  return seedHostOwnedFormState(
    flowState,
    steps,
    hostInitialFormState,
    entryStepId,
    jumpToStepId,
  ).flowState;
}

/** Same write as {@link seedInitialFormState}, plus the keys it actually applied. */
export function seedHostOwnedFormState(
  flowState: FlowState,
  steps: readonly Step[],
  hostInitialFormState: Readonly<Record<string, string>>,
  entryStepId?: string,
  jumpToStepId?: string,
): HostOwnedFormSeed {
  if (Object.keys(hostInitialFormState).length === 0) {
    return { flowState, appliedKeysByStep: {} };
  }

  const appliedKeysByStep: Record<string, readonly string[]> = {};
  const next = resolveLandingStepIds(steps, entryStepId, jumpToStepId).reduce(
    (state, stepId) => {
      const step = steps.find((candidate) => candidate.id === stepId);
      const seeded = seedOwnedKeys(state, stepId, step, hostInitialFormState);
      if (seeded.applied.length > 0) {
        appliedKeysByStep[stepId] = seeded.applied;
      }
      return seeded.flowState;
    },
    flowState,
  );

  return { flowState: next, appliedKeysByStep };
}

/**
 * DSM seed for the step that is on screen.
 *
 * Owned keys arrive through `deriveInitialFormState` (they are already
 * in flowState). Unbound host keys are merged only on the entry step and
 * the jump-to landing step, and only when that step has no recorded
 * selection for the key. A later step does not see the host seed, so a
 * Data Source Manager remount cannot put it back.
 *
 * `schoolId` keeps its existing precedence: a recorded step value wins,
 * otherwise the dedicated prop wins over a host seed of the same key.
 *
 * `appliedHostKeys` lists values taken from the host for this landing step.
 * A recorded, DCS, or preselected winner is omitted, and a later step
 * contributes nothing — the same `formKey` there is an ordinary answer.
 */
export function mergeLandingHostFormSeed(
  args: LandingHostFormSeedArgs,
): Record<string, string> {
  return resolveLandingHostFormSeed(args).formState;
}

export interface LandingHostFormSeedArgs {
  readonly seed: Record<string, string>;
  readonly hostInitialFormState: Readonly<Record<string, string>> | undefined;
  readonly currentStepId: string | undefined;
  readonly entryStepId: string | undefined;
  readonly jumpToStepId: string | undefined;
  readonly steps: readonly Step[];
  readonly flowState: FlowState | undefined;
  readonly schoolId: string | undefined;
  /**
   * Owned keys {@link seedHostOwnedFormState} wrote onto this step.
   * They are already inside `seed`, so the unbound merge cannot see them.
   */
  readonly ownedAppliedHostKeys?: readonly string[];
}

export interface LandingHostFormSeed {
  readonly formState: Record<string, string>;
  readonly appliedHostKeys: ReadonlySet<string>;
}

export function resolveLandingHostFormSeed(
  args: LandingHostFormSeedArgs,
): LandingHostFormSeed {
  const next = { ...args.seed };
  const applied = new Set<string>();
  const recorded = new Set(Object.keys(next));
  const current = args.currentStepId
    ? args.flowState?.[args.currentStepId]
    : undefined;
  recordedKeys(current).forEach((key) => {
    recorded.add(key);
  });

  const isLanding =
    typeof args.currentStepId === 'string' &&
    args.currentStepId.length > 0 &&
    resolveLandingStepIds(
      args.steps,
      args.entryStepId,
      args.jumpToStepId,
    ).includes(args.currentStepId);

  if (isLanding && args.hostInitialFormState) {
    for (const [key, value] of Object.entries(args.hostInitialFormState)) {
      if (recorded.has(key)) continue;
      next[key] = value;
      applied.add(key);
    }
    for (const key of args.ownedAppliedHostKeys ?? []) {
      if (next[key] === args.hostInitialFormState[key]) applied.add(key);
    }
  }

  if (args.schoolId && !recorded.has('schoolId')) {
    next.schoolId = args.schoolId;
    applied.delete('schoolId');
  }

  return { formState: next, appliedHostKeys: applied };
}

function seedOwnedKeys(
  flowState: FlowState,
  stepId: string,
  step: Step | undefined,
  hostInitialFormState: Readonly<Record<string, string>>,
): { readonly flowState: FlowState; readonly applied: readonly string[] } {
  const index = buildBoundComponentIndex(dslContent(step));
  return Object.entries(hostInitialFormState).reduce(
    (acc, [formKey, value]) => {
      const bound = index[formKey];
      if (!bound) return acc;
      if (recordedKeys(acc.flowState[stepId]).has(formKey)) return acc;
      return {
        flowState: appendOwnedOption(
          acc.flowState,
          stepId,
          optionForBound(bound, formKey, value),
        ),
        applied: [...acc.applied, formKey],
      };
    },
    {
      flowState,
      applied: [] as readonly string[],
    },
  );
}

function optionForBound(
  bound: BoundComponentInfo,
  formKey: string,
  value: string,
): Option {
  return buildOptionFromFormState({
    formKey,
    value,
    label: value,
    leadFieldAssociated: bound.leadFieldAssociated ?? formKey,
    customField: bound.customField,
    isArray: bound.isArray,
  });
}

function dslContent(step: Step | undefined): unknown {
  if (!step) return undefined;
  const dslConfig = (
    step as Step & { dslConfig?: { data?: { content?: unknown } } }
  ).dslConfig;
  return dslConfig?.data?.content;
}

function recordedKeys(
  data: FlowState[string] | undefined,
): ReadonlySet<string> {
  const keys = new Set<string>();
  if (!data) return keys;
  if (
    typeof data.leadFieldAssociated === 'string' &&
    data.leadFieldAssociated.length > 0
  ) {
    keys.add(data.leadFieldAssociated);
  }
  const option = data.option as Option | Option[] | undefined;
  const options = Array.isArray(option) ? option : option ? [option] : [];
  for (const entry of options) {
    if (entry.optionId) keys.add(String(entry.optionId));
    if (entry.leadFieldAssociated) keys.add(entry.leadFieldAssociated);
    if (entry.customField) keys.add(entry.customField);
  }
  return keys;
}

function appendOwnedOption(
  flowState: FlowState,
  stepId: string,
  option: Option,
): FlowState {
  const existing = flowState[stepId];
  if (!existing?.option) {
    return {
      ...flowState,
      [stepId]: { ...(existing ?? {}), option },
    };
  }

  if (Array.isArray(existing.option)) {
    return {
      ...flowState,
      [stepId]: { ...existing, option: [...existing.option, option] },
    };
  }

  const stepLeadField =
    typeof existing.leadFieldAssociated === 'string'
      ? existing.leadFieldAssociated
      : undefined;
  const current = existing.option.leadFieldAssociated
    ? existing.option
    : stepLeadField
      ? { ...existing.option, leadFieldAssociated: stepLeadField }
      : existing.option;
  const { leadFieldAssociated: _stepLeadField, ...rest } = existing;
  return {
    ...flowState,
    [stepId]: { ...rest, option: [current, option] },
  };
}
